import React, { useState } from "react";
import { X, AlertCircle } from "lucide-react";

/**
 * Iter220 · Modal opcional al descartar una oportunidad en Mercado.
 * Recoge motivos preset (chips multi-select) + comentario libre.
 * Estos datos van al audit log para que el comité detecte patrones.
 *
 * Props:
 *  - open (bool)
 *  - opp: {id, title/objeto, cpv, expediente}
 *  - onCancel()
 *  - onConfirm({motivo_descarte: string[], comment: string})
 */
export const MOTIVOS_DESCARTE_LABELS = {
  no_es_valoriza:         "No es actividad Valoriza",
  cpv_erroneo:            "CPV mal informado en el pliego",
  importe_bajo:           "Importe insuficiente",
  zona_no_activa:         "Fuera de zona activa",
  duplicado:              "Duplicado de otra oferta",
  menor_o_negociado:      "Menor / negociado sin publicidad",
  prorroga_o_modificado:  "Prórroga o modificado",
  solvencia_alta:         "Solvencia inalcanzable",
  cliente_no_estrategico: "Cliente no estratégico",
  otros:                  "Otros (usa el comentario)",
};

export function DescarteModal({ open, opp, onCancel, onConfirm }) {
  const [motivos, setMotivos] = useState([]);
  const [comment, setComment] = useState("");

  if (!open || !opp) return null;

  const toggleMotivo = (key) => {
    setMotivos(m => m.includes(key) ? m.filter(x => x !== key) : [...m, key]);
  };

  const handleConfirm = () => {
    onConfirm({ motivo_descarte: motivos, comment: comment.trim() });
    setMotivos([]);
    setComment("");
  };

  const handleCancel = () => {
    setMotivos([]);
    setComment("");
    onCancel();
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      data-testid="descarte-modal"
      onClick={(e) => { if (e.target === e.currentTarget) handleCancel(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-5 py-3 flex items-center gap-3">
          <div className="p-1.5 bg-white/15 rounded-lg"><AlertCircle size={16} /></div>
          <div className="flex-1">
            <h3 className="text-base font-semibold">Descartar oportunidad</h3>
            <p className="text-[11px] opacity-85">Feedback opcional para el comité (te ayuda a detectar patrones)</p>
          </div>
          <button onClick={handleCancel} className="text-white/80 hover:text-white text-xl leading-none" data-testid="descarte-close">×</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Título de la opp */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Oportunidad</div>
            <div className="text-sm text-slate-800 line-clamp-2">{(opp.title || opp.objeto || "—")}</div>
            {(opp.cpv || opp.expediente) && (
              <div className="text-[10px] text-slate-500 mt-1 font-mono">
                {opp.cpv && <span>CPV {opp.cpv}</span>}
                {opp.cpv && opp.expediente && <span> · </span>}
                {opp.expediente && <span>Exp. {opp.expediente}</span>}
              </div>
            )}
          </div>

          {/* Motivos chips */}
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-2 block">
              ¿Por qué la descartas? <span className="text-gray-400 font-normal">(opcional · selecciona todas las que apliquen)</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(MOTIVOS_DESCARTE_LABELS).map(([key, label]) => {
                const active = motivos.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleMotivo(key)}
                    data-testid={`descarte-motivo-${key}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${
                      active
                        ? "bg-red-100 text-red-800 border-red-300 shadow-sm"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                    }`}>
                    {active && <X size={10} className="inline mr-1" />}
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Comentario libre */}
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">
              Nota adicional <span className="text-gray-400 font-normal">(opcional · máx 500 caracteres)</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 500))}
              rows={2}
              placeholder="Ej: El pliego menciona vertedero pero es realmente sólo alquiler de maquinaria…"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-red-400 resize-y"
              data-testid="descarte-comment"
            />
            {comment && (
              <div className="text-[10px] text-gray-400 text-right mt-0.5">{comment.length}/500</div>
            )}
          </div>
        </div>

        <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-between gap-3 bg-gray-50">
          <div className="text-[10px] text-gray-500 max-w-[45%]">
            Quedará registrado en el audit log con tu usuario y timestamp.
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-white text-gray-700"
              data-testid="descarte-cancel"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700"
              data-testid="descarte-confirm"
            >
              Descartar {motivos.length > 0 && `(${motivos.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
